import {users as usersShared} from '@cl/shared';
import {creativesApi} from 'common/src/modules/creatives/creatives.api';
import {DEFAULT_CREATIVE_COUNTRY} from 'common/src/modules/creatives/creatives.consts';
import {doesUserHaveRole} from 'common/src/modules/roles/utils/doesUserHaveRole';
import {Routes} from 'common/src/modules/routing/routing.config';
import routingService from 'common/src/modules/routing/routing.service';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {RoleTypeName} from 'common/src/modules/users/users.types';
import {z} from 'zod';
import {CreativesManagerAgentToolName} from '../creativesManagerAgent.enums';

const inputSchema = z.object({
  name: z.string().describe('Name for the new creative'),
  templateId: z.number().describe('ID of the template to create the creative from'),
});

function createCreativeFromTemplateTool() {
  async function execute({
    input: {name, templateId},
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    const userHasCreativesWriteRole = doesUserHaveRole(
      usersShared.userSubject.data,
      RoleTypeName.CreativesWrite,
    );

    if (!userHasCreativesWriteRole) {
      return toolError('You do not have permission to create creatives.');
    }

    try {
      const creativeId = await creativesApi.createFromTemplate({
        name,
        templateId,
        country: DEFAULT_CREATIVE_COUNTRY,
      });

      routingService.goTo({
        url: Routes.CreativeDynamicParameters,
        pathParams: {creativeId},
      });

      return toolResult(
        `Successfully created creative "${name}" with ID ${creativeId} from template ${templateId}`,
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);

      return toolError(`Failed to create creative from template: ${message}`);
    }
  }

  return tool({
    name: CreativesManagerAgentToolName.CreateCreativeFromTemplate,
    description: 'Create a new creative from an existing template',
    displayName: 'Creating creative from template',
    execute,
    inputSchema,
  });
}

export default createCreativeFromTemplateTool;
